import { sum } from 'lodash-es'
import { useMemo } from 'react'

import {
  CoverOption,
  getActiveCoverLevelOption,
  getAdditionalItemsTotals,
  Quote,
} from '../domain'
import { PaymentScheduleType, ProductConfig } from '../state/configuration'
import {
  useAdditionalPremiumBreakdown,
  useBasePremiums,
  useCoverOptions,
  usePromotion,
} from './covers'

export interface QuotePremiums {
  paymentSchedule: PaymentScheduleType | undefined
  breakdown: {
    base: number
    discount: number
  }
  additionalBreakdown: Record<string, number>
  totals: {
    gross: number
    term: number
    discounted: number
    taxAmount: number
  }
  optional: CoverOption[]
}

export const useQuotePremiums = (
  quote: Quote | undefined,
  product: ProductConfig,
): QuotePremiums => {
  const basePremiums = useBasePremiums(quote)

  const allCoverOptions = useCoverOptions(quote, product)
  const allAdditionalItems = useAdditionalPremiumBreakdown(quote)
  const promotion = usePromotion(quote)

  return useMemo(() => {
    const paymentSchedule = quote
      ? getActiveCoverLevelOption(quote)?.paymentScheduleType
      : undefined

    const coverOptions = allCoverOptions.filter((it) => it.active)
    const additionalItems = allAdditionalItems.filter((it) => it.active)

    const basePremium = sum(basePremiums.map((it) => it.premium))
    const baseTotalPremium = sum(basePremiums.map((it) => it.totalPremium))

    const coverOptionPremium = sum(coverOptions.map((it) => it.premium))
    const coverOptionTotalPremium = sum(coverOptions.map((it) => it.totalPremium))

    const discountPremium = promotion?.discountAmt ?? 0

    const baseTaxAmount = sum(basePremiums.map((it) => it.taxAmount))
    const coverOptionTaxAmount = sum(coverOptions.map((it) => it.taxAmount))

    const grossPremium = basePremium + coverOptionPremium
    const grossTotalPremium = baseTotalPremium + coverOptionTotalPremium
    const discountedTotal = grossPremium - discountPremium
    const taxAmountTotal = baseTaxAmount + coverOptionTaxAmount

    const additionalItemsTotals = getAdditionalItemsTotals(additionalItems)

    return {
      paymentSchedule,
      breakdown: {
        base: basePremium,
        discount: discountPremium,
      },
      additionalBreakdown: additionalItemsTotals,
      totals: {
        gross: grossPremium,
        term: grossTotalPremium,
        discounted: discountedTotal,
        taxAmount: taxAmountTotal,
      },
      optional: coverOptions,
    } satisfies QuotePremiums
  }, [basePremiums, allCoverOptions, promotion, allAdditionalItems])
}
